// Feilklassifisering — ett sted, så hver skjerm reagerer likt på samme feil.
//
// HVORFOR DETTE ER EN EGEN, TESTET FIL:
// Kontrakten (services/dugnad/API.md) definerer statuskodene og formen
// `{ feil, felt? }`. Et UI som bare skriver «noe gikk galt» kaster bort den
// informasjonen, og et UI som viser tomme lister ved feil LYVER — brukeren kan
// ikke skille «du har ingen økter» fra «vi fikk ikke svar». Derfor oversettes
// hvert svar til en art som skjermene kan handle på, og hver art har en ærlig
// norsk tekst.
//
// ENDEPUNKTER SOM IKKE FINNES ENNÅ er en egen art. Sprint-, kanban- og
// registrerings-rutene bygges parallelt med denne klienten; til de er ute svarer
// tjenesten `404 { feil: "ukjent endepunkt" }`. Det skal si nettopp det, ikke
// «fant ikke sprinten» — og aldeles ikke en tom, tilforlatelig tavle.

export type FeilArt =
  /** Svaret kom aldri fram (nede, brutt samband, CORS). */
  | 'nett'
  /** 401 — token mangler, er utløpt eller trukket tilbake. */
  | 'uautentisert'
  /** 403 — pålogget, men mangler rettigheten. */
  | 'forbudt'
  /** 404 på en ressurs som burde finnes. */
  | 'finnesIkke'
  /** 404 fordi tjenesten ikke kjenner ruten — funksjonen er ikke bygget/utrullet. */
  | 'endepunktMangler'
  /** 400 — ugyldig inndata. `felt` peker på ruta som skal markeres. */
  | 'ugyldig'
  /** 409 — konflikt (brukt lenke, opptatt ansattnummer, godkjent økt). */
  | 'konflikt'
  /** 429 — for mange forsøk. */
  | 'throttlet'
  /** 503 — funksjonen er ikke konfigurert i drift (f.eks. manglende admin-token). */
  | 'ikkeKonfigurert'
  /** 5xx forøvrig. */
  | 'tjenerfeil'
  | 'ukjent';

export interface ApiFeil {
  art: FeilArt;
  /** HTTP-status. 0 betyr at svaret aldri kom fram. */
  status: number;
  /** Norsk tekst som kan vises ordrett. Tjenestens egen tekst har forrang. */
  melding: string;
  /** Feltnavn fra `{ felt }`, når tjenesten peker på et skjemafelt. */
  felt?: string;
  /** Er det vits i en «Prøv igjen»-knapp? Nei for feil brukeren må rette selv. */
  kanPrøvePåNytt: boolean;
}

/** Arter der en ny forsøk kan lykkes uten at noe annet endres. */
const PRØV_IGJEN: ReadonlySet<FeilArt> = new Set<FeilArt>(['nett', 'tjenerfeil', 'throttlet']);

const STANDARDTEKST: Record<FeilArt, string> = {
  nett: 'Fikk ikke svar fra tjenesten. Sjekk sambandet og prøv igjen.',
  uautentisert: 'Tokenet er ikke gyldig lenger. Logg inn på nytt.',
  forbudt: 'Du har ikke rettighet til dette.',
  finnesIkke: 'Fant ikke det du ba om.',
  endepunktMangler: 'Tjenesten kjenner ikke dette endepunktet — funksjonen er ikke tilgjengelig i '
    + 'denne versjonen av dugnad-tjenesten.',
  ugyldig: 'Tjenesten avviste inndata.',
  konflikt: 'Handlingen kom i konflikt med noe som alt finnes.',
  throttlet: 'For mange forsøk. Vent litt og prøv igjen.',
  ikkeKonfigurert: 'Funksjonen er ikke satt opp i drift.',
  tjenerfeil: 'Tjenesten svarte med en feil.',
  ukjent: 'Uventet svar fra tjenesten.',
};

function artFor(status: number, tekst: string): FeilArt {
  if (status === 0) return 'nett';
  if (status === 400) return 'ugyldig';
  if (status === 401) return 'uautentisert';
  if (status === 403) return 'forbudt';
  // Kontrakten har én 404 for «ukjent endepunkt» og én for «ukjent økt/sprint/kort».
  // Skillet er hele forskjellen mellom «ikke bygget ennå» og «finnes ikke».
  if (status === 404) return /ukjent endepunkt/i.test(tekst) ? 'endepunktMangler' : 'finnesIkke';
  if (status === 409) return 'konflikt';
  if (status === 429) return 'throttlet';
  if (status === 503) return 'ikkeKonfigurert';
  if (status >= 500) return 'tjenerfeil';
  return 'ukjent';
}

/**
 * HTTP-status + kropp → `ApiFeil`.
 *
 * Tjenestens egen `feil`-tekst brukes når den finnes: den er skrevet på norsk for
 * mennesker (kontrakten krever det), og den vet mer om saken enn vi gjør her.
 * Vår standardtekst er reservefallet, ikke førstevalget.
 */
export function klassifiserFeil(status: number, kropp: unknown): ApiFeil {
  const k = (kropp ?? null) as { feil?: unknown; felt?: unknown } | null;
  const fraTjenesten = typeof k?.feil === 'string' && k.feil.trim() ? k.feil.trim() : '';
  const art = artFor(status, fraTjenesten);
  // Ved «ukjent endepunkt» er tjenestens tekst for knapp for en bruker («ukjent
  // endepunkt» sier ingenting om hva som mangler), så da vinner vår forklaring.
  const melding = art === 'endepunktMangler' || !fraTjenesten ? STANDARDTEKST[art] : fraTjenesten;
  const felt = typeof k?.felt === 'string' && k.felt ? k.felt : undefined;
  return { art, status, melding, ...(felt ? { felt } : {}), kanPrøvePåNytt: PRØV_IGJEN.has(art) };
}

/** Kastet unntak fra `fetch` (ingen respons i det hele tatt). */
export function nettverksfeil(årsak: unknown): ApiFeil {
  const detalj = årsak instanceof Error && årsak.message ? ` (${årsak.message})` : '';
  return {
    art: 'nett',
    status: 0,
    melding: STANDARDTEKST.nett + detalj,
    kanPrøvePåNytt: true,
  };
}

/** Svar som ikke var JSON der JSON var lovet. */
export function ugyldigSvarfeil(status: number): ApiFeil {
  return {
    art: 'ukjent',
    status,
    melding: `Tjenesten svarte ${status} med noe som ikke er JSON. `
      + 'Står det en mellomtjener (proxy) mellom klienten og tjenesten?',
    kanPrøvePåNytt: true,
  };
}
